/**
 * ledger — Kế hoạch: xác nhận dự chi/dự thu, giao dịch định kỳ, ngân sách phong bì (GĐ3).
 * Mọi lệnh ghi là một Firestore transaction; UI không gọi Firestore trực tiếp.
 */
import { getFirestore, collection, doc, runTransaction, Timestamp } from 'firebase/firestore';
import { APP_ID } from '../config/constants';
import { isQuotaError, QUOTA_MESSAGE } from './errors';
import { LedgerError, type LedgerResult, addExpenseFromPlan, addIncomeFromPlan } from './ledger';
import { followsLast, followedAmount, previousPeriod } from '../finance/recurring';

type Data = Record<string, any>;
const base = (uid: string) => ['artifacts', APP_ID, 'users', uid] as const;

const guard = async (fn: () => Promise<Partial<LedgerResult>>): Promise<LedgerResult> => {
  try { return { success: true, ...(await fn()) }; }
  catch (e) {
    if (e instanceof LedgerError) return { success: false, error: e.message };
    if (isQuotaError(e)) return { success: false, error: QUOTA_MESSAGE };
    console.error('Plans error:', e);
    return { success: false, error: `Không ghi được (chưa có gì thay đổi): ${(e as Error).message}` };
  }
};

// ───────── Xác nhận / huỷ dự chi – dự thu ─────────
export interface ConfirmOverrides { amount?: number; walletId?: string; walletName?: string; transactionDate?: string }

/** Xác nhận dự chi → tạo khoản chi thật + trừ ví + đóng dự chi, CÙNG một transaction (chống xác nhận hai lần). */
export const confirmPlannedExpense = (userId: string, plannedId: string, o: ConfirmOverrides = {}): Promise<LedgerResult> =>
  addExpenseFromPlan(userId, plannedId, (p) => ({
    amount: o.amount ?? p.amount, category: p.category || p.categoryName, categoryName: p.categoryName || p.category,
    walletId: o.walletId ?? p.walletId, walletName: o.walletName ?? p.walletName,
    description: p.note || `Xác nhận từ dự chi: ${p.category ?? ''}`,
    transactionDate: o.transactionDate ?? p.plannedDate, tags: p.tags ?? [], attachments: p.attachments ?? []
  }));

export const confirmPlannedIncome = (userId: string, plannedId: string, o: ConfirmOverrides = {}): Promise<LedgerResult> =>
  addIncomeFromPlan(userId, plannedId, (p) => ({
    amount: o.amount ?? p.amount, category: p.category || p.categoryName, categoryName: p.categoryName || p.category,
    walletId: o.walletId ?? p.walletId, walletName: o.walletName ?? p.walletName,
    description: p.description || p.notes || `Xác nhận từ dự thu: ${p.category ?? ''}`,
    incomeSourceId: p.incomeSourceId, incomeSourceName: p.incomeSourceName, status: 'active',
    transactionDate: o.transactionDate ?? p.plannedDate, tags: p.tags ?? [], attachments: p.attachments ?? []
  }));

/** Huỷ (không xoá cứng): giữ lại khoản kèm lý do để còn dấu vết. */
export const cancelPlanned = (userId: string, kind: 'expense' | 'income', plannedId: string, reason?: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const ref = doc(db, a, b, c, d, kind === 'income' ? 'plannedIncomes' : 'plannedExpenses', plannedId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new LedgerError('Không tìm thấy khoản dự kiến.');
      if (snap.data().status !== 'pending') throw new LedgerError('Chỉ huỷ được khoản đang chờ xác nhận.');
      tx.update(ref, { status: 'cancelled', ...(reason?.trim() ? { cancelReason: reason.trim().slice(0, 200) } : {}), cancelledAt: Timestamp.now(), updatedAt: Timestamp.now() });
    });
    return {};
  });

// ───────── Khoản dự chi/dự thu đơn lẻ ─────────
export interface PlannedInput {
  amount: number; category: string; walletId: string; walletName?: string;
  /** yyyy-mm-dd */ date: string; note?: string;
  /** Khoản của một mẫu định kỳ (tạo cùng lúc với mẫu): để xác nhận lệch số còn cập nhật được mẫu */
  templateId?: string;
}
const parseISO = (s: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '');
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  if (!d || Number.isNaN(d.getTime()) || d.getMonth() !== Number(m![2]) - 1) throw new LedgerError('Ngày không hợp lệ.');
  return d;
};
const checkPlanned = (i: PlannedInput) => {
  if (!(i.amount > 0)) throw new LedgerError('Số tiền phải lớn hơn 0.');
  if (!i.category) throw new LedgerError('Chọn danh mục.');
  if (!i.walletId) throw new LedgerError('Chọn ví.');
  return parseISO(i.date);
};
const plannedCol = (kind: 'expense' | 'income') => (kind === 'income' ? 'plannedIncomes' : 'plannedExpenses');

export const addPlanned = (userId: string, kind: 'expense' | 'income', i: PlannedInput): Promise<LedgerResult> =>
  guard(async () => {
    const d = checkPlanned(i);
    const db = getFirestore(); const [a, b, c, e] = base(userId);
    const ref = doc(collection(db, a, b, c, e, plannedCol(kind)));
    const ts = Timestamp.now();
    await runTransaction(db, async (tx) => {
      tx.set(ref, {
        amount: Math.round(i.amount), status: 'pending', plannedDate: Timestamp.fromDate(d), year: d.getFullYear(), month: d.getMonth() + 1,
        category: i.category, categoryName: i.category, walletId: i.walletId, walletName: i.walletName ?? '',
        [kind === 'income' ? 'description' : 'note']: i.note?.trim() ?? '', isRecurring: !!i.templateId, plannedBy: userId, tags: [], attachments: [],
        ...(i.templateId ? { templateId: i.templateId, recurringDay: d.getDate(), recurringKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` } : {}),
        [kind === 'income' ? 'actualIncomeId' : 'actualExpenseId']: null, confirmedBy: null, confirmedAt: null, createdAt: ts, updatedAt: ts
      });
    });
    return { id: ref.id };
  });

/** Sửa khoản ĐANG CHỜ (đổi ngày/số tiền/ví/danh mục/ghi chú). Khoản đã xác nhận hoặc huỷ không sửa được. */
export const updatePlanned = (userId: string, kind: 'expense' | 'income', id: string, i: PlannedInput): Promise<LedgerResult> =>
  guard(async () => {
    const d = checkPlanned(i);
    const db = getFirestore(); const [a, b, c, e] = base(userId);
    const ref = doc(db, a, b, c, e, plannedCol(kind), id);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new LedgerError('Không tìm thấy khoản dự kiến.');
      if (snap.data().status !== 'pending') throw new LedgerError('Chỉ sửa được khoản đang chờ xác nhận.');
      tx.update(ref, {
        amount: Math.round(i.amount), plannedDate: Timestamp.fromDate(d), year: d.getFullYear(), month: d.getMonth() + 1,
        category: i.category, categoryName: i.category, walletId: i.walletId, walletName: i.walletName ?? '',
        [kind === 'income' ? 'description' : 'note']: i.note?.trim() ?? '', updatedAt: Timestamp.now()
      });
    });
    return {};
  });


// ───────── Dự kiến trả thẻ / nợ ─────────
export interface PlannedPaymentInput {
  paymentType: 'credit' | 'debt';
  /** id thẻ (credit) hoặc khoản vay (debt) */
  targetId: string; targetName?: string;
  amount: number; walletId: string; walletName?: string;
  /** yyyy-mm-dd */ date: string; note?: string; templateId?: string;
}
const checkPayment = (i: PlannedPaymentInput) => {
  if (i.paymentType !== 'credit' && i.paymentType !== 'debt') throw new LedgerError('Loại thanh toán không hợp lệ.');
  if (!i.targetId) throw new LedgerError(i.paymentType === 'credit' ? 'Chọn thẻ tín dụng.' : 'Chọn khoản vay.');
  if (!(i.amount > 0)) throw new LedgerError('Số tiền phải lớn hơn 0.');
  if (!i.walletId) throw new LedgerError('Chọn ví trả.');
  return parseISO(i.date);
};

export const addPlannedPayment = (userId: string, i: PlannedPaymentInput): Promise<LedgerResult> =>
  guard(async () => {
    const d = checkPayment(i);
    const db = getFirestore(); const [a, b, c, e] = base(userId);
    const ref = doc(collection(db, a, b, c, e, 'plannedPayments'));
    const ts = Timestamp.now();
    await runTransaction(db, async (tx) => {
      tx.set(ref, {
        year: d.getFullYear(), month: d.getMonth() + 1, plannedDate: Timestamp.fromDate(d), paymentType: i.paymentType,
        ...(i.paymentType === 'credit' ? { creditCardId: i.targetId, creditCardName: i.targetName ?? '' } : { debtId: i.targetId, debtName: i.targetName ?? '' }),
        sourceWalletId: i.walletId, sourceWalletName: i.walletName ?? '', amount: Math.round(i.amount), note: i.note?.trim() ?? '',
        status: 'pending', actualPaymentId: null, confirmedAt: null, confirmedBy: null, plannedBy: userId,
        isRecurring: !!i.templateId, recurringDay: d.getDate(),
        ...(i.templateId ? { templateId: i.templateId, recurringKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` } : {}),
        createdAt: ts, updatedAt: ts
      });
    });
    return { id: ref.id };
  });

/** Sửa khoản trả thẻ/nợ ĐANG CHỜ: số tiền, ví trả, ngày, ghi chú (không đổi thẻ/khoản vay). */
export const updatePlannedPayment = (userId: string, id: string, i: { amount: number; walletId: string; walletName?: string; date: string; note?: string }): Promise<LedgerResult> =>
  guard(async () => {
    if (!(i.amount > 0)) throw new LedgerError('Số tiền phải lớn hơn 0.');
    if (!i.walletId) throw new LedgerError('Chọn ví trả.');
    const d = parseISO(i.date);
    const db = getFirestore(); const [a, b, c, e] = base(userId);
    const ref = doc(db, a, b, c, e, 'plannedPayments', id);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new LedgerError('Không tìm thấy khoản dự kiến.');
      if (snap.data().status !== 'pending') throw new LedgerError('Chỉ sửa được khoản đang chờ xác nhận.');
      tx.update(ref, {
        amount: Math.round(i.amount), sourceWalletId: i.walletId, sourceWalletName: i.walletName ?? '', note: i.note?.trim() ?? '',
        plannedDate: Timestamp.fromDate(d), year: d.getFullYear(), month: d.getMonth() + 1, updatedAt: Timestamp.now()
      });
    });
    return {};
  });

export const cancelPlannedPayment = (userId: string, id: string, reason?: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore(); const [a, b, c, e] = base(userId);
    const ref = doc(db, a, b, c, e, 'plannedPayments', id);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new LedgerError('Không tìm thấy khoản dự kiến.');
      if (snap.data().status !== 'pending') throw new LedgerError('Chỉ huỷ được khoản đang chờ xác nhận.');
      tx.update(ref, { status: 'cancelled', ...(reason?.trim() ? { cancelReason: reason.trim().slice(0, 200) } : {}), cancelledAt: Timestamp.now(), updatedAt: Timestamp.now() });
    });
    return {};
  });

/** Mở lại khoản đã bỏ qua (bỏ qua nhầm): về trạng thái chờ xác nhận, xoá lý do. Khoản chưa bị bỏ qua thì từ chối. */
export const reopenCancelled = (userId: string, collectionName: 'plannedExpenses' | 'plannedIncomes' | 'plannedPayments', id: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore(); const [a, b, c, e] = base(userId);
    const ref = doc(db, a, b, c, e, collectionName, id);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new LedgerError('Không tìm thấy khoản dự kiến.');
      if (snap.data().status !== 'cancelled') throw new LedgerError('Chỉ mở lại được khoản đã bỏ qua.');
      const now = Timestamp.now();
      tx.update(ref, { status: 'pending', cancelReason: null, cancelledAt: null, reopenedAt: now, updatedAt: now });
    });
    return {};
  });

/** Mở lại các khoản dự kiến "đã xác nhận" nhưng giao dịch sinh ra đã không còn (màn Kiểm tra toàn vẹn). Một transaction cho cả lô. */
export const reopenPlannedBatch = (userId: string, collectionName: 'plannedExpenses' | 'plannedIncomes' | 'plannedPayments', ids: string[]): Promise<LedgerResult> =>
  guard(async () => {
    if (ids.length === 0) throw new LedgerError('Chưa chọn khoản nào.');
    if (ids.length > 200) throw new LedgerError('Mở lại tối đa 200 khoản mỗi lần.');
    const db = getFirestore(); const [a, b, c, e] = base(userId);
    const key = collectionName === 'plannedExpenses' ? 'actualExpenseId' : collectionName === 'plannedIncomes' ? 'actualIncomeId' : 'actualPaymentId';
    let count = 0;
    await runTransaction(db, async (tx) => {
      count = 0;
      const now = Timestamp.now();
      const refs = ids.map(id => doc(db, a, b, c, e, collectionName, id));
      const snaps = await Promise.all(refs.map(r => tx.get(r)));
      snaps.forEach((snap, i) => {
        if (snap.exists() && (snap.data() as Data).status === 'confirmed') {
          tx.update(refs[i], { status: 'pending', [key]: null, confirmedAt: null, confirmedBy: null, reopenedAt: now, updatedAt: now });
          count++;
        }
      });
    });
    return { count };
  });

// ───────── Ngân sách phong bì ─────────
/** `key` dạng yyyy-mm. Thay thế toàn bộ hạn mức của tháng. */
export const saveEnvelope = (userId: string, key: string, input: { limits: Record<string, number>; rollover?: boolean }): Promise<LedgerResult> =>
  guard(async () => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(key)) throw new LedgerError('Tháng không hợp lệ.');
    const limits: Record<string, number> = {};
    for (const [k, v] of Object.entries(input.limits)) {
      const n = Math.round(Number(v));
      if (!k.trim()) continue;
      if (!Number.isFinite(n) || n < 0) throw new LedgerError(`Hạn mức của "${k}" không hợp lệ.`);
      if (n > 0) limits[k] = n; // hạn mức 0 = bỏ khỏi ngân sách
    }
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const ref = doc(db, a, b, c, d, 'budgetEnvelopes', key);
    await runTransaction(db, async (tx) => {
      tx.set(ref, { limits, rollover: !!input.rollover, updatedAt: Timestamp.now() });
    });
    return { id: key };
  });

// ───────── Giao dịch định kỳ ─────────
export interface TemplateInput {
  type: 'income' | 'expense' | 'payment';
  name: string; amount: number; category: string; walletId: string; walletName?: string;
  frequency: 'monthly' | 'yearly'; dayOfMonth?: number; monthOfYear?: number; active: boolean;
  /** yyyy-mm: không sinh khoản cho các tháng trước tháng này (tránh trùng khoản đã có từ bản cũ) */
  startMonth?: string;
  /** Chỉ cho type 'payment' (trả thẻ/nợ): walletId = ví nguồn thanh toán */
  paymentType?: 'credit' | 'debt'; creditCardId?: string; creditCardName?: string; debtId?: string; debtName?: string;
  /** Tự lấy số ĐÃ XÁC NHẬN của kỳ trước làm số dự kiến kỳ này (mặc định bật cho mẫu trả thẻ/nợ) */
  followLastActual?: boolean;
}

const PAYMENT_CATEGORY = 'Trả thẻ & nợ';

/** Chuẩn hoá + kiểm tra một mẫu; ném LedgerError nếu sai. Dùng chung cho lưu lẻ và nhập hàng loạt. */
const templateData = (t: TemplateInput, now: Timestamp): Data => {
  if (!t.name?.trim()) throw new LedgerError('Nhập tên khoản định kỳ.');
  if (!(t.amount > 0)) throw new LedgerError('Số tiền phải lớn hơn 0.');
  if (t.type !== 'payment' && !t.category) throw new LedgerError('Chọn danh mục.');
  if (!t.walletId) throw new LedgerError('Chọn ví.');
  if (t.startMonth && !/^\d{4}-(0[1-9]|1[0-2])$/.test(t.startMonth)) throw new LedgerError('Tháng bắt đầu không hợp lệ.');
  const data: Data = {
    type: t.type, name: t.name.trim(), amount: Math.round(t.amount), category: t.type === 'payment' ? PAYMENT_CATEGORY : t.category,
    walletId: t.walletId, walletName: t.walletName ?? '', frequency: t.frequency,
    dayOfMonth: Math.min(31, Math.max(1, Math.round(t.dayOfMonth || 1))), active: !!t.active, updatedAt: now
  };
  if (t.frequency === 'yearly') data.monthOfYear = Math.min(12, Math.max(1, Math.round(t.monthOfYear || 1)));
  if (t.startMonth) data.startMonth = t.startMonth;
  if (t.followLastActual !== undefined) data.followLastActual = !!t.followLastActual;
  if (t.type === 'payment') {
    if (t.paymentType !== 'credit' && t.paymentType !== 'debt') throw new LedgerError('Loại thanh toán không hợp lệ.');
    if (t.paymentType === 'credit' && !t.creditCardId) throw new LedgerError('Chọn thẻ tín dụng.');
    if (t.paymentType === 'debt' && !t.debtId) throw new LedgerError('Chọn khoản nợ.');
    data.paymentType = t.paymentType;
    if (t.paymentType === 'credit') { data.creditCardId = t.creditCardId; data.creditCardName = t.creditCardName ?? ''; }
    else { data.debtId = t.debtId; data.debtName = t.debtName ?? ''; }
  }
  return data;
};

export const saveTemplate = (userId: string, id: string | null, t: TemplateInput): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const ref = id ? doc(db, a, b, c, d, 'recurringTemplates', id) : doc(collection(db, a, b, c, d, 'recurringTemplates'));
    const now = Timestamp.now();
    const data = templateData(t, now);
    await runTransaction(db, async (tx) => {
      const snap = id ? await tx.get(ref) : null;
      if (id && !snap!.exists()) throw new LedgerError('Không tìm thấy khoản định kỳ.');
      tx.set(ref, id ? { ...snap!.data(), ...data } : { ...data, createdAt: now });
    });
    return { id: ref.id };
  });

/** Nhập NHIỀU mẫu trong một transaction: hoặc tất cả được tạo, hoặc không mẫu nào. */
export const importTemplates = (userId: string, inputs: TemplateInput[]): Promise<LedgerResult> =>
  guard(async () => {
    if (inputs.length === 0) throw new LedgerError('Chưa chọn khoản nào.');
    if (inputs.length > 200) throw new LedgerError('Nhập tối đa 200 khoản mỗi lần.');
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const now = Timestamp.now();
    const rows = inputs.map(t => ({ ref: doc(collection(db, a, b, c, d, 'recurringTemplates')), data: templateData(t, now) }));
    await runTransaction(db, async (tx) => { for (const r of rows) tx.set(r.ref, { ...r.data, createdAt: now }); });
    return { count: rows.length };
  });

export const deleteTemplate = (userId: string, id: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    await runTransaction(db, async (tx) => { tx.delete(doc(db, a, b, c, d, 'recurringTemplates', id)); });
    return {};
  });

/** Dữ liệu khoản dự kiến sinh từ một mẫu cho (year, month). Dùng chung cho sinh tự động và "xác nhận ngay" khoản chưa sinh. */
const plannedColOf = (t: Data) => (t.type === 'income' ? 'plannedIncomes' : t.type === 'payment' ? 'plannedPayments' : 'plannedExpenses');

const plannedFromTemplate = (userId: string, t: Data & { id: string }, year: number, month: number, ts: Timestamp, followed: number | null = null): { col: string; id: string; data: Data } => {
  const key = `${year}-${String(month).padStart(2, '0')}`;
  const day = Math.min(Math.max(1, Number(t.dayOfMonth) || 1), new Date(year, month, 0).getDate());
  const col = t.type === 'income' ? 'plannedIncomes' : t.type === 'payment' ? 'plannedPayments' : 'plannedExpenses';
  const plannedDate = Timestamp.fromDate(new Date(year, month - 1, day));
  if (t.type === 'payment') {
    return {
      col, id: `${t.id}_${key}`, data: {
        year, month, plannedDate, paymentType: t.paymentType,
        ...(t.paymentType === 'credit' ? { creditCardId: t.creditCardId, creditCardName: t.creditCardName ?? '' } : { debtId: t.debtId, debtName: t.debtName ?? '' }),
        sourceWalletId: t.walletId, sourceWalletName: t.walletName ?? '', amount: followed ?? Number(t.amount), note: t.name,
        ...(followed !== null ? { amountFollowed: true } : {}),
        status: 'pending', actualPaymentId: null, confirmedAt: null, confirmedBy: null, plannedBy: userId,
        isRecurring: true, recurringDay: day, templateId: t.id, recurringKey: key, createdAt: ts, updatedAt: ts
      }
    };
  }
  return {
    col, id: `${t.id}_${key}`, data: {
      amount: followed ?? Number(t.amount), status: 'pending', plannedDate,
      ...(followed !== null ? { amountFollowed: true } : {}),
      year, month, category: t.category, categoryName: t.category, walletId: t.walletId, walletName: t.walletName ?? '',
      [t.type === 'income' ? 'description' : 'note']: t.name, isRecurring: true, recurringDay: day,
      templateId: t.id, recurringKey: key, plannedBy: userId, tags: [], attachments: [],
      [t.type === 'income' ? 'actualIncomeId' : 'actualExpenseId']: null, confirmedBy: null, confirmedAt: null,
      createdAt: ts, updatedAt: ts
    }
  };
};

/**
 * Sinh khoản dự chi/dự thu của THÁNG HIỆN TẠI từ các mẫu định kỳ đang bật.
 * Id xác định `<mẫu>_<yyyy-mm>` + kiểm tra tồn tại trong transaction → mở app ở hai tab/thiết bị
 * cùng lúc cũng chỉ tạo MỘT bản ghi; khoản đã xác nhận/huỷ không bị tạo lại hay ghi đè.
 * Không bù tháng đã qua (không bịa quá khứ). Người dùng vẫn phải bấm xác nhận — không tự trừ tiền.
 */
export const generateRecurring = (userId: string, templates: Array<Data & { id: string }>, now: Date = new Date()): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const year = now.getFullYear(), month = now.getMonth() + 1;
    const key = `${year}-${String(month).padStart(2, '0')}`;
    let created = 0;

    for (const t of templates) {
      if (!t.active || !(Number(t.amount) > 0) || !t.walletId || !t.category) continue;
      if (t.startMonth && key < t.startMonth) continue;
      if (t.frequency === 'yearly' && (Number(t.monthOfYear) || 1) !== month) continue;
      const ref = doc(db, a, b, c, d, plannedColOf(t), `${t.id}_${key}`);
      const pp = previousPeriod(t, year, month);
      const prevRef = followsLast(t) ? doc(db, a, b, c, d, plannedColOf(t), `${t.id}_${pp.year}-${String(pp.month).padStart(2, '0')}`) : null;
      await runTransaction(db, async (tx) => {
        const prevSnap = prevRef ? await tx.get(prevRef) : null;
        if ((await tx.get(ref)).exists()) return;
        const { data } = plannedFromTemplate(userId, t, year, month, Timestamp.now(), followedAmount(t, prevSnap?.exists() ? (prevSnap.data() as Data) : null));
        tx.set(ref, data);
        created++;
      });
    }
    return { count: created };
  });

/**
 * Tạo ngay MỘT khoản dự kiến từ mẫu cho (year, month) để xác nhận được liền (khoản "Sẽ được tạo").
 * Id xác định như bộ sinh tự động nên không bao giờ tạo trùng; đã có thì giữ nguyên.
 */
export const materializeFromTemplate = (userId: string, t: Data & { id: string }, year: number, month: number): Promise<LedgerResult> =>
  guard(async () => {
    if (!(Number(t.amount) > 0)) throw new LedgerError('Mẫu chưa có số tiền.');
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const id = `${t.id}_${key}`;
    const ref = doc(db, a, b, c, d, plannedColOf(t), id);
    const pp = previousPeriod(t, year, month);
    const prevRef = followsLast(t) ? doc(db, a, b, c, d, plannedColOf(t), `${t.id}_${pp.year}-${String(pp.month).padStart(2, '0')}`) : null;
    await runTransaction(db, async (tx) => {
      const prevSnap = prevRef ? await tx.get(prevRef) : null;
      if ((await tx.get(ref)).exists()) return;
      const { data } = plannedFromTemplate(userId, t, year, month, Timestamp.now(), followedAmount(t, prevSnap?.exists() ? (prevSnap.data() as Data) : null));
      tx.set(ref, data);
    });
    return { id };
  });

/**
 * Xoá khoản vừa được tạo từ mẫu (materializeFromTemplate) mà người dùng chưa làm gì với nó:
 * vẫn đang chờ và chưa từng được sửa (updatedAt = createdAt). Bấm Xác nhận/Sửa/Bỏ qua rồi đóng hộp không để lại bản ghi thừa.
 */
export const discardUntouched = (userId: string, col: 'plannedExpenses' | 'plannedIncomes' | 'plannedPayments', id: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const ref = doc(db, a, b, c, d, col, id);
    let removed = false;
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return;
      const x = snap.data() as Data;
      const same = x.createdAt?.toMillis && x.updatedAt?.toMillis ? x.createdAt.toMillis() === x.updatedAt.toMillis() : false;
      if (x.status === 'pending' && x.isRecurring && x.templateId && same) { tx.delete(ref); removed = true; }
    });
    return { count: removed ? 1 : 0 };
  });

// ───────── Mục tiêu tài chính ─────────
export interface GoalInput {
  name: string; targetAmount: number; currentAmount?: number; deadline?: string | null;
  category?: string; description?: string; expectedReturn?: number | null;
}

export const saveGoal = (userId: string, id: string | null, g: GoalInput): Promise<LedgerResult> =>
  guard(async () => {
    if (!g.name?.trim()) throw new LedgerError('Nhập tên mục tiêu.');
    if (!(g.targetAmount > 0)) throw new LedgerError('Số tiền mục tiêu phải lớn hơn 0.');
    if ((g.currentAmount ?? 0) < 0) throw new LedgerError('Số đã có không được âm.');
    if (g.deadline && Number.isNaN(new Date(g.deadline).getTime())) throw new LedgerError('Hạn chót không hợp lệ.');
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const ref = id ? doc(db, a, b, c, d, 'goals', id) : doc(collection(db, a, b, c, d, 'goals'));
    const now = Timestamp.now();
    const data: Data = {
      name: g.name.trim(), targetAmount: Math.round(g.targetAmount), currentAmount: Math.round(g.currentAmount ?? 0),
      deadline: g.deadline || null, category: g.category ?? '', description: g.description?.trim() ?? '',
      expectedReturn: g.expectedReturn ?? null, updatedAt: now
    };
    await runTransaction(db, async (tx) => {
      const snap = id ? await tx.get(ref) : null;
      if (id && !snap!.exists()) throw new LedgerError('Không tìm thấy mục tiêu.');
      tx.set(ref, id ? { ...snap!.data(), ...data } : { ...data, createdAt: now });
    });
    return { id: ref.id };
  });

/** Ghi nhận số đã để dành thêm cho mục tiêu — chỉ cập nhật tiến độ, KHÔNG chuyển tiền giữa các ví. */
export const contributeGoal = (userId: string, id: string, amount: number): Promise<LedgerResult> =>
  guard(async () => {
    if (!Number.isFinite(amount) || amount === 0) throw new LedgerError('Nhập số tiền khác 0.');
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    const ref = doc(db, a, b, c, d, 'goals', id);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new LedgerError('Không tìm thấy mục tiêu.');
      const next = (Number(snap.data().currentAmount) || 0) + Math.round(amount);
      if (next < 0) throw new LedgerError('Số đã có không thể âm — kiểm tra lại số tiền rút.');
      tx.update(ref, { currentAmount: next, updatedAt: Timestamp.now() });
    });
    return {};
  });

export const deleteGoal = (userId: string, id: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore();
    const [a, b, c, d] = base(userId);
    await runTransaction(db, async (tx) => { tx.delete(doc(db, a, b, c, d, 'goals', id)); });
    return {};
  });
